import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { createCoupleAccount, clearError } from '../../store/slices/authSlice';
import { storeCoupleAuth } from '../../lib/CoupleAuthentication';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Heart, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

interface SignupFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

export default function SignupForm({ onSuccess, onSwitchToLogin }: SignupFormProps) {
  const dispatch = useAppDispatch();
  const { isCreatingAccount, error } = useAppSelector(state => state.auth);
  
  const [formData, setFormData] = useState({
    partner1_name: '',
    partner2_name: ''
  });

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear any previous errors when user starts typing
    if (error) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    const partner1 = formData.partner1_name.trim();
    const partner2 = formData.partner2_name.trim();
    
    if (!partner1 || !partner2) {
      toast.error('Please enter both partner names');
      return;
    }

    if (partner1.length < 2 || partner2.length < 2) {
      toast.error('Partner names must be at least 2 characters long');
      return;
    }

    if (partner1.toLowerCase() === partner2.toLowerCase()) {
      toast.error('Partner names must be different');
      return;
    }

    // Check for valid characters
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    if (!nameRegex.test(partner1)) {
      toast.error('First partner name contains invalid characters');
      return;
    }
    
    if (!nameRegex.test(partner2)) {
      toast.error('Second partner name contains invalid characters');
      return;
    }

    try {
      const result = await dispatch(createCoupleAccount({
        partner1_name: partner1,
        partner2_name: partner2
      })).unwrap();

      // Store authentication data in localStorage
      storeCoupleAuth(result);

      toast.success('Account created successfully! Welcome to your expense tracking journey together!');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is already handled by the rejected case in authSlice
      console.error('Account creation failed:', error);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto" data-testid="signup-form">
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-primary/10 rounded-full">
            <Heart className="h-8 w-8 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl">Create Your Account</CardTitle>
        <CardDescription>
          Start tracking expenses together as a couple
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive" data-testid="signup-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="partner1_name">First Partner's Name</Label>
            <Input
              id="partner1_name"
              type="text"
              placeholder="Enter first partner's name"
              value={formData.partner1_name}
              onChange={handleInputChange('partner1_name')}
              disabled={isCreatingAccount}
              data-testid="partner1-name-input"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Use your real first name or preferred name
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="partner2_name">Second Partner's Name</Label>
            <Input
              id="partner2_name"
              type="text"
              placeholder="Enter second partner's name"
              value={formData.partner2_name}
              onChange={handleInputChange('partner2_name')}
              disabled={isCreatingAccount}
              data-testid="partner2-name-input"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Your partner's real first name or preferred name
            </p>
          </div>
          
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="font-medium text-sm mb-2">How it works:</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Both partners can log in using these same names</li>
              <li>• You'll share one account for tracking expenses</li>
              <li>• All data is synced between both partners</li>
              <li>• Names are case-insensitive for login</li>
            </ul>
          </div>
          
          <Button
            type="submit"
            className="w-full"
            disabled={isCreatingAccount}
            data-testid="signup-submit-button"
          >
            {isCreatingAccount ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Create Account
              </>
            )}
          </Button>
        </form>
        
        {onSwitchToLogin && (
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-primary hover:underline font-medium"
                data-testid="switch-to-login-button"
              >
                Log in here
              </button>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}